/**
 * @description 表格排序逻辑Hook
 * @date 2025-01-17
 * @author lv.chen
 */

import { useEffect, useState } from 'react';
import { SortType } from '@home/enums/common';

import type { ISortConfig, ISortChangeEvent, IPublicFeedbackParams } from '../types';

interface IUseTableSortParams {
  /** 当前数据源决定默认排序字段 */
  mode: 'di' | 'alinea';
  /** 反馈查询参数 */
  feedbackParams: IPublicFeedbackParams;
  /** 获取反馈历史数据的方法 */
  fetchTrueDataFeedbackHistory: (params: IPublicFeedbackParams) => Promise<void>;
  /** 设置页码的方法 */
  setPageIndex: (pageIndex: number) => void;
}

interface IUseTableSortReturn {
  /** 排序配置 */
  sortConfig: ISortConfig;
  /** 排序变更处理函数 */
  handleSortChange: (event: ISortChangeEvent) => void;
}

/**
 * 表格排序逻辑Hook
 */
export default function useTableSort(params: IUseTableSortParams): IUseTableSortReturn {
  const { mode, feedbackParams, fetchTrueDataFeedbackHistory, setPageIndex } = params;

  /** Alinea 的 Date 与 Databrain 的 metric_date 是两套接口字段，切换来源时不能复用旧排序键。 */
  const defaultSortKey = mode === 'alinea' ? 'date' : 'metric_date';

  const [sortConfig, setSortConfig] = useState<ISortConfig>({
    sortKey: defaultSortKey,
    sortVal: feedbackParams?.order || SortType.Desc,
  });

  useEffect(() => {
    setSortConfig({
      sortKey: defaultSortKey,
      sortVal: SortType.Desc,
    });
  }, [defaultSortKey]);

  /**
   * 处理排序变更
   */
  const handleSortChange = (event: ISortChangeEvent): void => {
    setSortConfig({
      sortKey: event?.key,
      sortVal: event?.sortVal,
    });

    // 排序时重置到第一页
    setPageIndex(1);

    const requestParams: IPublicFeedbackParams = {
      ...feedbackParams,
      order_metric: event?.key,
      order: event?.sortVal,
      page: 1,
    };

    fetchTrueDataFeedbackHistory(requestParams);
  };

  return {
    sortConfig,
    handleSortChange,
  };
}
